'use client';

import { Reveal } from '@/components/products/Reveal';
import { problem, solution } from '@/lib/corporate-content';

/**
 * The case, in two moves: what is broken, and what we build instead.
 *
 * Layout family: asymmetric editorial split — a large ghost-stroked numeral
 * column against a text column. Deliberately different from every other
 * section on the page (DESIGN.md: no two sections share a layout family).
 */
export function ProblemSolutionSection() {
  return (
    <section
      className="self-styled relative z-20 border-b border-white/[0.07]"
      style={{ backgroundColor: 'rgba(10,10,10,0.72)' }}
      aria-labelledby="problem-heading"
    >
      <div className="content-padding">
        {/* ── Problem ── */}
        <div className="grid grid-cols-1 gap-10 pb-16 pt-24 md:pb-20 md:pt-32 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
          <Reveal>
            <span className="eyebrow">{problem.eyebrow}</span>
            <h2
              id="problem-heading"
              className="font-six-caps mt-6 uppercase leading-[0.88] text-white"
              style={{ fontSize: 'clamp(2.75rem, 6vw, 5.25rem)' }}
            >
              {problem.title}
            </h2>
          </Reveal>
          <Reveal delay={90}>
            <p className="max-w-2xl text-base font-light leading-relaxed text-white/60 md:text-lg md:leading-relaxed">
              {problem.body}
            </p>
          </Reveal>
        </div>

        {/* ── Solution ── */}
        <div className="border-t border-white/[0.07] pb-24 pt-16 md:pb-32 md:pt-20">
          <Reveal>
            <span className="eyebrow">{solution.eyebrow}</span>
            <h2
              className="font-six-caps mt-6 max-w-4xl uppercase leading-[0.88] text-white"
              style={{ fontSize: 'clamp(2.75rem, 6vw, 5.25rem)' }}
            >
              {solution.title}
            </h2>
            <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-white/60 md:text-lg md:leading-relaxed">
              {solution.body}
            </p>
          </Reveal>

          {/* Numbered hairline list — not a card grid. */}
          <ul className="mt-14 flex flex-col md:mt-20">
            {solution.points.map((point, i) => (
              <li key={point.label}>
                <Reveal delay={i * 70}>
                  <div className="grid grid-cols-1 gap-3 border-t border-white/10 py-7 md:grid-cols-[4rem_minmax(0,15rem)_1fr] md:items-baseline md:gap-8 md:py-9">
                    <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/35">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <h3 className="text-base font-medium leading-snug text-white md:text-lg">
                      {point.label}
                    </h3>
                    <p className="max-w-2xl text-sm font-light leading-relaxed text-white/55 md:text-[15px]">
                      {point.text}
                    </p>
                  </div>
                </Reveal>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
